<template>
  <div class="app-header" :style="headerStyle">
    <!-- 左侧区域 -->
    <div class="header-left">
      <slot name="left">
        <button 
          v-if="showBack" 
          class="back-btn" 
          @click="handleBack"
          :aria-label="$t('common.back')"
        >
          <img src="@/assets/images/mine/left.webp" class="w-full h-full"  />
        </button>
      </slot>
    </div>

    <!-- 中间标题区域 -->
    <div class="header-center">
      <slot name="center">
        <h1 v-if="title" class="header-title">{{ title }}</h1>
      </slot>
    </div>

    <!-- 右侧区域 -->
    <div class="header-right">
      <slot name="right">
        <button 
          v-if="showClose" 
          class="close-btn" 
          @click="handleClose"
          :aria-label="$t('common.close')"
        >
          <Icon name="carbon:close" class="close-icon" />
        </button>
      </slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useRouter } from 'vue-router'
import { theme } from '~/styles/theme'

// 定义组件属性
interface Props {
  title?: string // 标题文本
  showBack?: boolean // 是否显示返回按钮
  showClose?: boolean // 是否显示关闭按钮
  backgroundColor?: string // 自定义背景颜色
  textColor?: string // 自定义文字颜色
  height?: string // 自定义高度
  fixed?: boolean // 是否固定定位
  transparent?: boolean // 是否透明背景
  border?: boolean // 是否显示底部边框
  safeArea?: boolean // 是否适配安全区域
}

const props = withDefaults(defineProps<Props>(), {
  title: '',
  showBack: false,
  showClose: false,
  backgroundColor: theme.colors.background, // 使用主题颜色
  textColor: theme.colors.textPrimary,
  height: '44px',
  fixed: true,
  transparent: false,
  border: false,
  safeArea: true
})

// 定义事件
interface Emits {
  back: []
  close: []
}

const emit = defineEmits<Emits>()

const router = useRouter()

// 计算样式
const headerStyle = computed(() => {
  const style: Record<string, string> = {
    height: props.height,
    color: props.textColor
  }

  if (props.transparent) {
    style.backgroundColor = 'transparent'
  } else {
    style.backgroundColor = props.backgroundColor
  }

  if (props.fixed) {
    style.position = 'fixed'
    style.top = '0'
    style.left = '0'
    style.right = '0'
    style.zIndex = theme.zIndex.fixed.toString()
  }

  if (props.border) {
    style.borderBottom = `1px solid ${theme.colors.border}`
  }

  return style
})

// 处理返回按钮点击
const handleBack = () => {
  emit('back')
  if (router) {
    router.back()
  }
}

// 处理关闭按钮点击
const handleClose = () => {
  emit('close')
}
</script>

<style scoped>
.app-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 16px;
  box-sizing: border-box;
  background-color: v-bind('theme.colors.background');
  color: v-bind('theme.colors.textPrimary');
  font-size: v-bind('theme.fontSizes.md');
  font-weight: v-bind('theme.fontWeights.medium');
  min-height: 44px;
  position: relative;
  transition: v-bind('theme.transitions.normal');
}

.header-left,
.header-center,
.header-right {
  display: flex;
  align-items: center;
  flex: 1;
}

.header-left {
  justify-content: flex-start;
}

.header-center {
  justify-content: center;
  flex: 2;
}

.header-right {
  justify-content: flex-end;
}

.header-title {
  font-size: 14px;
  font-weight: v-bind('theme.fontWeights.semibold');
  margin: 0;
  color: #fff;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
}

.back-btn,
.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 35px;
  height: 35px;
  border: none;
  background: transparent;
  color: inherit;
  cursor: pointer;
  border-radius: v-bind('theme.borderRadius.small');
  transition: v-bind('theme.transitions.fast');
  padding: 0;
}

.back-btn:hover,
.close-btn:hover {
  background-color: v-bind('theme.colors.overlayLight');
}

.back-btn:active,
.close-btn:active {
  background-color: v-bind('theme.colors.overlay');
}

.back-icon,
.close-icon {
  width: 20px;
  height: 20px;
  color: inherit;
}

/* 响应式设计 */
@media (max-width: 375px) {
  .app-header {
    padding: 0 12px;
  }
  
  .header-title {
    font-size: 16px;
    max-width: 150px;
  }
}

/* 安全区域适配 */
@supports (padding: max(0px)) {
  .app-header {
    padding-left: max(16px, env(safe-area-inset-left));
    padding-right: max(16px, env(safe-area-inset-right));
    padding-top: max(0px, env(safe-area-inset-top));
  }
}
</style>
